import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import { createPortal } from "react-dom";

import { menuPosition } from "./menuPosition";

const MENU_MIN_WIDTH = 180;
const MENU_Z_INDEX = 10000;

const menuBaseStyle = {
  backgroundColor: "#1a1a1a",
  border: "1px solid rgba(255, 255, 255, 0.08)",
  borderRadius: "8px",
  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.5)",
  minWidth: `${MENU_MIN_WIDTH}px`,
  padding: "4px",
};

// `onOpen` fires as the menu opens, for work that has to happen before the user
// clicks an item rather than after — Copy Image warms its blob there, because
// Safari treats a tap's user activation as spent across an await.
const AdminActionDropDown = ({ options, setAction, updateItem, onClick, onOpen }) => {
  const [shown, setShown] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);

  const getTriggerEl = useCallback(() => {
    return triggerRef.current?.querySelector(".dropdown") ?? triggerRef.current;
  }, []);

  const updateMenuPosition = useCallback(() => {
    const triggerEl = getTriggerEl();
    if (!triggerEl) return;

    const rect = triggerEl.getBoundingClientRect();
    // Measured, not assumed: the menu is already in the DOM (rendered `visibility: hidden` until
    // there is a position for it), so its real box is readable on this first pass.
    const menuHeight = wrapperRef.current?.offsetHeight || 120;
    const menuWidth = wrapperRef.current?.offsetWidth || MENU_MIN_WIDTH;
    const { left, top, maxWidth } = menuPosition({
      trigger: rect,
      menuWidth,
      menuHeight,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
    });

    setMenuStyle({
      position: "fixed",
      top,
      left,
      right: "auto",
      maxWidth,
      zIndex: MENU_Z_INDEX,
    });
  }, [getTriggerEl]);

  useLayoutEffect(() => {
    if (!shown) {
      setMenuStyle(null);
      return;
    }

    updateMenuPosition();

    requestAnimationFrame(updateMenuPosition);
  }, [shown, options, updateMenuPosition]);

  useEffect(() => {
    if (!shown) return undefined;

    const onReposition = () => updateMenuPosition();
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);

    return () => {
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [shown, updateMenuPosition]);

  useEffect(() => {
    const handleClick = (e) => {
      if (
        wrapperRef.current?.contains(e.target) ||
        triggerRef.current?.contains(e.target)
      ) {
        return;
      }
      setShown(false);
    };

    if (shown) {
      document.addEventListener("mousedown", handleClick);
      document.addEventListener("touchstart", handleClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("touchstart", handleClick);
    };
  }, [shown]);

  const onDropdownClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Outside the updater: StrictMode invokes updaters twice in development, and
    // this one has a side effect.
    if (!shown) onOpen?.();
    setShown((prev) => !prev);
  };

  const onAction = (action) => {
    setShown(false);
    updateItem(action);
  };

  const menu =
    shown &&
    createPortal(
      <div
        ref={wrapperRef}
        className="dropdown-content dropdown-content--portal"
        // Before there is a position for it the menu is rendered hidden — but OUT OF FLOW, and
        // that is load-bearing: `updateMenuPosition` measures `offsetWidth` on this pass, and a
        // static block-level div is as wide as the body, not as wide as its own longest option.
        // Measuring that would place the menu against the wrong edge on first paint.
        style={{
          ...menuBaseStyle,
          ...(menuStyle || { visibility: "hidden", position: "fixed", top: 0, left: 0 }),
        }}
      >
        {options.map((option, no) => (
          <div key={no + "dropdown-option_parent_key"}>
            {option.options.map((subOption, subOptionIndex) => (
              <div
                className="dropdown-option clickable"
                onClick={() => onAction(subOption.id)}
                key={subOptionIndex + "_sub"}
                style={{
                  backgroundColor: "transparent",
                  boxShadow: "none",
                  padding: "8px 12px",
                  cursor: "pointer",
                  color: "#e4e4e4",
                  fontSize: "13px",
                  borderRadius: "6px",
                  transition: "background-color 120ms ease, color 120ms ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.08)";
                  e.currentTarget.style.color = "#ffffff";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.color = "#e4e4e4";
                }}
              >
                {subOption.name}
              </div>
            ))}
          </div>
        ))}
      </div>,
      document.body
    );

  return (
    <>
      <div
        className={`button-with-dropdown${shown ? " button-with-dropdown--open" : ""}`}
        style={{ textAlign: "left", position: "relative" }}
      >
        {/* Flex item between .button-with-dropdown and the button. Without a
            class it shrink-wraps, so VIEW never fills its column and the ⋮
            (absolute to .button-with-dropdown) lands outside the pill. */}
        <div ref={triggerRef} className="button-with-dropdown__trigger">
          <button type="button" className="d-flex justify-content-end button-width-dropdown">
            <span className="align-self-center label opacity-100" onClick={onClick}>
              View
            </span>
            <span
              className="dropdown align-self-center"
              onClick={onDropdownClick}
              role="button"
              tabIndex={0}
              aria-expanded={shown}
              aria-haspopup="menu"
            />
          </button>
        </div>
      </div>
      {menu}
    </>
  );
};

export default AdminActionDropDown;
